import { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import Card from '../../common/components/Card';
import DateRangePicker from '../../common/components/DateRangePicker';
import Button from '../../common/components/Button';
import DataGrid from '../../common/components/DataGrid';
import TextLoader from '../../common/components/TextLoader';
import { GET_TIMESHEET_WEEKLY_SNAPSHOT, UPDATE_USER_SETTINGS, GET_MY_SETTINGS, GET_PROJECT_CLASSIFICATIONS } from '../queries';
import { fetchTimesheetsForPeriod } from '../index';
import { SegmentedControl } from '@mantine/core';
import { AgGridColumn } from '@ag-grid-community/react';
import client from '../../../api/graphql/client';
import { useQuery } from 'react-apollo';
import { replaceTokens } from '../utils/tokenProcessor';
import { extractAndEnrichUserData, getUserDataForRow, getAvailableTokenFields } from '../utils/userDataExtractor';
import { createGridData, processPTOAllocation } from '../utils/ptoAllocationUtils';
import { PTOOverrideModal } from './PTOOverrideModal';

const PTOAllocationReport = () => {
  const { data, refetch } = useQuery(GET_MY_SETTINGS);
  const userId = data?.me?._id;
  const userSettings = data?.me?.settings;
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState(null);
  const [startDate, setStartDate] = useState(moment().startOf('isoWeek').format('YYYYMMDD'));
  const [endDate, setEndDate] = useState(moment().endOf('isoWeek').format('YYYYMMDD'));
  const [rowData, setRowData] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [unsavedOverrides, setUnsavedOverrides] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [modalOpened, setModalOpened] = useState(false);
  const [enrichedUserMap, setEnrichedUserMap] = useState(new Map());
  const [availableTokenFields, setAvailableTokenFields] = useState([]);
  const [projectClassifications, setProjectClassifications] = useState({});
  const [viewMode, setViewMode] = useState('allocated'); // 'allocated' or 'original'
  const exportRef = useRef();
  const onExportReady = ({ getExportFunc }) => {
    exportRef.current = getExportFunc();
  };
  const handleExportClick = () => {
    if (exportRef.current) exportRef.current();
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const fetchedData = await fetchTimesheetsForPeriod({
        startDate,
        endDate,
        setLoadingText,
        query: GET_TIMESHEET_WEEKLY_SNAPSHOT,
      });
      // Remove tasks for days that fall outside of the selected range
      const timesheetsWithTrimmedTasks = fetchedData.map(timesheet => {
        const newLineItems = timesheet.lineItems.map(lineItem => {
          for (const property in lineItem) {
            if (property.endsWith('Task') && lineItem[property]) {
              const { date } = lineItem[property];
              if (date < startDate || date > endDate) {
                delete lineItem[property];
              }
            }
          }
          return lineItem;
        });
        return {
          ...timesheet,
          lineItems: newLineItems,
        };
      });
      const IndividualTaskrow = createGridData(timesheetsWithTrimmedTasks);
      const flattenedRows = IndividualTaskrow.flat();
      
      // Extract unique project IDs
      const uniqueProjectIds = [...new Set(flattenedRows.map(row => row.projectId).filter(Boolean))];
      
      // Fetch project classifications
      if (uniqueProjectIds.length > 0) {
        try {
          const { data: classificationData } = await client.query({
            query: GET_PROJECT_CLASSIFICATIONS,
            variables: { projectIds: uniqueProjectIds }
          });
          
          // Create a map of project ID to classification
          const classificationsMap = {};
          if (classificationData?.finance?.projects) {
            classificationData.finance.projects.forEach(project => {
              if (project._id && project.classification) {
                classificationsMap[project._id] = project.classification;
              }
            });
          }
          setProjectClassifications(classificationsMap);
        } catch (error) {
          console.error('Error fetching project classifications:', error);
        }
      }
      
      // Always fetch enriched user data to populate department and other user fields
      let userMap = new Map();
      if (flattenedRows.length > 0) {
        userMap = await extractAndEnrichUserData(flattenedRows);
        setEnrichedUserMap(userMap);
        
        // Populate department from enriched user data if missing
        flattenedRows.forEach(row => {
          if (row.email && userMap.has(row.email)) {
            const enrichedUser = userMap.get(row.email);
            if (!row.department && enrichedUser.department) {
              row.department = enrichedUser.department;
            }
          }
        });
      }
      
      // Store raw rows - PTO allocation will be applied after overrides
      setRowData(flattenedRows);
      
      // Get available token fields
      if (flattenedRows.length > 0) {
        const tokenFields = getAvailableTokenFields(flattenedRows[0], userMap);
        setAvailableTokenFields(tokenFields);
      }
      
      setLoading(false);
    };
    fetchData().catch(console.error);
  }, [startDate, endDate, overrides]);

  // Load overrides from user settings on mount and when settings change
  useEffect(() => {
    if (!userSettings) return;
    
    const val = userSettings.timesheetDailySnapshotOverrides;
    if (val && val !== '[]') {
      try {
        const parsedOverrides = JSON.parse(val);
        setOverrides(parsedOverrides);
        setUnsavedOverrides(parsedOverrides);
      } catch (e) {
        console.error('Error parsing overrides:', e);
      }
    } else {
      // Reset to empty if no overrides found
      setOverrides([]);
      setUnsavedOverrides([]);
    }
  }, [userSettings]);

  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(JSON.stringify(overrides) !== JSON.stringify(unsavedOverrides));
  }, [overrides, unsavedOverrides]);

  // Manual save function
  const handleSaveOverrides = async () => {
    if (!userId) return;
    try {
      await client.mutate({
        mutation: UPDATE_USER_SETTINGS,
        variables: {
          id: userId,
          input: {
            timesheetDailySnapshotOverrides: JSON.stringify(overrides),
          },
        },
      });
      await refetch();
      setUnsavedOverrides(overrides);
      setHasUnsavedChanges(false);
    } catch (e) {
      console.error('Failed to save overrides:', e);
    }
  };

  // Process row data with overrides and PTO allocation
  const getProcessedRowData = () => {
    // Step 1: Apply overrides to raw rows
    const rowsWithOverrides = rowData.map(row => {
      // Add classification to row data if not already present
      const rowWithClassification = {
        ...row,
        classification: row.classification || (row.projectId ? projectClassifications[row.projectId] || '' : '')
      };
      
      // Get user data for token processing
      const userData = getUserDataForRow(rowWithClassification, enrichedUserMap);
      
      // Find matching overrides (no token processing for conditions)
      const matchingOverrides = overrides.filter(ovr =>
        ovr.conditions.every(cond => String(rowWithClassification[cond.field]) === String(cond.value)),
      );
      
      if (matchingOverrides.length === 0) return rowWithClassification;
      
      // Process tokens in override values and merge
      const mergedOverride = matchingOverrides.reduce((acc, ovr) => {
        // Process tokens in override values
        const processedOverride = Object.entries(ovr.override).reduce((obj, [key, value]) => ({
          ...obj,
          [key]: replaceTokens(value, userData)
        }), {});
        
        return { ...acc, ...processedOverride };
      }, {});
      
      // Remove 'department' from mergedOverride - department should always come from user data, not overrides
      const { department: _, ...overrideWithoutDepartment } = mergedOverride;
      
      return { ...rowWithClassification, ...overrideWithoutDepartment, projectCodeAdj: rowWithClassification.department };
    });
    
    // Return original data or processed PTO allocation based on view mode
    if (viewMode === 'original') {
      return rowsWithOverrides;
    }
    
    // Step 2: Apply PTO allocation logic after overrides are processed
    return processPTOAllocation(rowsWithOverrides, projectClassifications);
  };

  return (
    <Card
      border
      title="PTO Allocation Report"
      floating
      padded={false}
      actionComponent={
        <div className="flex flex-col gap-2">
          <div className="flex items-between w-full gap-4">
            <div className="flex items-center gap-4">
              <SegmentedControl
                value={viewMode}
                onChange={setViewMode}
                data={[
                  { label: 'PTO Allocation', value: 'allocated' },
                  { label: 'Original Data', value: 'original' },
                ]}
              />
              <Button variant="outline" color="blue" onClick={() => setModalOpened(true)}>
                Manage Overrides
              </Button>
            </div>
            <div className="flex items-center gap-4">
              <DateRangePicker
                loading={loading ? true : undefined}
                handleDateChange={range => {
                  setStartDate(moment(range[0]).format('YYYYMMDD'));
                  setEndDate(moment(range[1]).format('YYYYMMDD'));
                }}
                startDate={startDate}
                endDate={endDate}
              />
              <div className="">
                <Button disabled={loading} onClick={handleExportClick}>
                  Export
                </Button>
              </div>
            </div>
          </div>
          <PTOOverrideModal
            opened={modalOpened}
            onClose={() => setModalOpened(false)}
            overrides={overrides}
            setOverrides={setOverrides}
            unsavedOverrides={unsavedOverrides}
            setUnsavedOverrides={setUnsavedOverrides}
            hasUnsavedChanges={hasUnsavedChanges}
            setHasUnsavedChanges={setHasUnsavedChanges}
            availableTokenFields={availableTokenFields}
            onSave={handleSaveOverrides}
          />
        </div>
      }
    >
      {loading ? (
        <TextLoader text={loadingText} />
      ) : (
        <DataGrid
          rowData={getProcessedRowData()}
          onExportReady={onExportReady}
          exportFileName={viewMode === 'original' 
            ? `TimesheetSnapshot-WeekEnding(${endDate})-ExportedOn(${moment(new Date()).format('YYYYMMDD-hhmm')}).csv`
            : `PTOAllocation-WeekEnding(${endDate})-ExportedOn(${moment(new Date()).format('YYYYMMDD-hhmm')}).csv`}
        >
          {viewMode === 'original' ? (
            <>
              <AgGridColumn field="endDate" width={125} />
              <AgGridColumn field="status" width={175} />
              <AgGridColumn field="lastName" />
              <AgGridColumn field="firstName" />
              <AgGridColumn field="payrollId" headerName="Payroll ID" />
              <AgGridColumn field="department" />
              <AgGridColumn field="email" />
              <AgGridColumn field="clientName" width={150} />
              <AgGridColumn field="project" />
              <AgGridColumn field="projectCode" />
              <AgGridColumn field="milestone" />
              <AgGridColumn field="deliverable" />
              <AgGridColumn field="taskdate" width={110} />
              <AgGridColumn field="hours" width={110} />
              <AgGridColumn field="role" />
              <AgGridColumn field="rate" />
              <AgGridColumn field="name" />
              <AgGridColumn field="totalCost" />
              <AgGridColumn field="supervisor" />
              <AgGridColumn field="approvers" />
              <AgGridColumn field="approvedOn" />
              <AgGridColumn field="comment" />
              <AgGridColumn field="classification" />
            </>
          ) : (
            <>
              <AgGridColumn field="lastName" headerName="Last Name" />
              <AgGridColumn field="firstName" headerName="First Name" />
              <AgGridColumn field="email" headerName="Email" />
              <AgGridColumn field="projectCodeAdj" headerName="Project Code Adj" />
              <AgGridColumn field="classificationAdj" headerName="Classification Adj" />
              <AgGridColumn 
                field="total" 
                headerName="Total" 
                width={100}
                valueFormatter={params => params.value != null ? Number(params.value).toFixed(2) : ''}
              />
              <AgGridColumn 
                field="allocation" 
                headerName="Allocation" 
                width={120}
                valueFormatter={params => params.value != null ? `${Number(params.value).toFixed(2)}%` : ''}
              />
            </>
          )}
        </DataGrid>
      )}
    </Card>
  );
};

export default PTOAllocationReport;
